/**
 * Theme Configuration - Traffic Light 4x color system
 */

export type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'failed';
export type TrafficColor = 'red' | 'orange' | 'yellow' | 'green' | 'cyan' | 'slate';
export type StatusType = 'success' | 'warning' | 'error' | 'info';
export type ThemeColor = TrafficColor | 'primary' | 'secondary' | 'accent';
export type SizeVariant = 'sm' | 'md' | 'lg';

export const TRAFFIC_COLORS: Record<TrafficColor, string> = {
  red: '#ef4444',
  orange: '#ea580c',
  yellow: '#eab308',
  green: '#22c55e',
  cyan: '#06b6d4',
  slate: '#64748b',
};

export const STATUS_CONFIG: Record<StatusType, { label: string; color: TrafficColor }> = {
  success: { label: 'Success', color: 'green' },
  warning: { label: 'Warning', color: 'yellow' },
  error: { label: 'Error', color: 'red' },
  info: { label: 'Info', color: 'cyan' },
};

export const RISK_CONFIG: Record<RiskLevel, { label: string; color: TrafficColor }> = {
  critical: { label: 'Critical', color: 'red' },
  high: { label: 'High', color: 'orange' },
  medium: { label: 'Medium', color: 'yellow' },
  low: { label: 'Low', color: 'green' },
  info: { label: 'Info', color: 'cyan' },
  failed: { label: 'N/A', color: 'slate' },
};

export function getRiskLevel(score: number): RiskLevel {
  if (score >= 9.0) return 'critical';
  if (score >= 7.0) return 'high';
  if (score >= 4.0) return 'medium';
  return 'low';
}

export function getRecommendationRiskLevel(recommendation: string): RiskLevel {
  switch (recommendation) {
    case 'SAFE':
    case 'approved':
      return 'low';
    case 'CONDITIONAL':
    case 'approved-monitoring':
      return 'medium';
    case 'CAUTION':
    case 'conditional':
      return 'high';
    case 'DO_NOT_INSTALL':
    case 'denied':
    case 'rejected':
      return 'critical';
    default:
      return 'medium';
  }
}

/**
 * Get risk level considering both recommendation AND overall score (GV3-104).
 * If the score contradicts the recommendation, use the higher-risk value.
 */
export function getEffectiveRiskLevel(recommendation: string, overallScore?: number): RiskLevel {
  const recLevel = getRecommendationRiskLevel(recommendation);
  if (overallScore == null) return recLevel;

  // Overall score is 0-100 (weighted composite: CVSS*10*0.4 + AIVSS*10*0.3 + AARS*0.3)
  let scoreLevel: RiskLevel;
  if (overallScore >= 80) scoreLevel = 'critical';
  else if (overallScore >= 60) scoreLevel = 'high';
  else if (overallScore >= 40) scoreLevel = 'medium';
  else scoreLevel = 'low';

  // Use whichever is more severe
  const severity: Record<RiskLevel, number> = { low: 0, info: 1, medium: 2, high: 3, critical: 4, failed: 5 };
  return severity[scoreLevel] > severity[recLevel] ? scoreLevel : recLevel;
}

/**
 * Finding-aware variant of getEffectiveRiskLevel for list views.
 * Mirrors the override logic in transformScanToInsights.getRecommendationType:
 * critical findings → 'critical', high findings → at least 'high'.
 * This keeps the list badges consistent with the detail page's recommendation display.
 */
export function getEffectiveRiskLevelWithFindings(
  recommendation: string,
  overallScore: number | undefined,
  findingSummary?: Record<string, unknown>
): RiskLevel {
  // Guard against null (DB rows can have null findingSummary despite the type)
  const summary = findingSummary ?? {};
  const criticalFindings = (summary.critical as number) ?? 0;
  const highFindings = (summary.high as number) ?? 0;
  const base = getEffectiveRiskLevel(recommendation, overallScore);
  const severity: Record<RiskLevel, number> = { low: 0, info: 1, medium: 2, high: 3, critical: 4, failed: 5 };
  if (criticalFindings > 0 && severity['critical'] > severity[base]) return 'critical';
  if (highFindings > 0 && severity['high'] > severity[base]) return 'high';
  return base;
}

export const SIZE_CONFIG: Record<SizeVariant, { padding: string; fontSize: string }> = {
  sm: { padding: '0.25rem 0.5rem', fontSize: '0.75rem' },
  md: { padding: '0.5rem 1rem', fontSize: '0.875rem' },
  lg: { padding: '0.75rem 1.5rem', fontSize: '1rem' },
};
